/**
 * The slot grid's own sort and filters.
 *
 * Zoom used to live here too. It is one app-wide preference now - see
 * utils/zoomPreference.js - because it is about somebody's eyes rather than
 * about the task, and eight copies of it meant setting it in one place left
 * every other place none the wiser. Sort and filter stay here, per place.
 *
 * Its own storage key, for the same reason the picker has one: zoom, sort and
 * filter are per-place in this app, and standing inside Base Set asking "which
 * of these am I still missing" is a different question from the order Explore
 * or the picker was left in. Sharing a key would mean working through a master
 * set quietly re-ordered two screens nobody was looking at.
 *
 * One sort, not two. A set grid is a fixed list in card order with no query
 * over it, so there is no Best Match to keep - `relevance` is meaningless
 * inside one set and the catalogue sorts do not offer it. The default is
 * `set-oldest`, which inside a single set collapses to the printed card number:
 * the order a collector expects when they open a binder.
 */

import {
  EMPTY_CATALOG_FILTERS,
  normaliseFilters,
} from '../../services/cardData/catalogFilters';

const STORAGE_KEY = 'cardchase.wishSetGrid.prefs';

/**
 * A chase binder keeps its own copy of the same four choices.
 *
 * Sharing the set grid's key would mean that ticking Missing only inside a
 * chase binder silently re-ordered every master set as well, which is the same
 * argument that gave the set grid a key of its own in the first place: zoom,
 * sort and filter are per-place in this app.
 */
const BINDER_STORAGE_KEY = 'cardchase.wishBinderGrid.prefs';

export const DEFAULT_SET_GRID_SORT = 'set-oldest';

export const DEFAULT_SET_GRID_PREFS = {
  sort: DEFAULT_SET_GRID_SORT,
  filters: EMPTY_CATALOG_FILTERS,
  // "Just what I am missing" is the most useful question this grid answers, so
  // it is remembered like any other filter rather than reset on every visit.
  missingOnly: false,
};

function readPrefs(key) {
  try {
    const raw = typeof localStorage === 'undefined' ? null : localStorage.getItem(key);
    if (!raw) return { ...DEFAULT_SET_GRID_PREFS };
    const saved = JSON.parse(raw) || {};
    return {
      sort: saved.sort || DEFAULT_SET_GRID_SORT,
      filters: normaliseFilters(saved.filters),
      missingOnly: !!saved.missingOnly,
    };
  } catch {
    // A corrupt preference must never be the reason a set will not open.
    return { ...DEFAULT_SET_GRID_PREFS };
  }
}

function writePrefs(key, prefs) {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(key, JSON.stringify({
      sort: prefs.sort || DEFAULT_SET_GRID_SORT,
      filters: normaliseFilters(prefs.filters),
      missingOnly: !!prefs.missingOnly,
    }));
  } catch {
    /* a full or blocked store is not worth an error on a grid */
  }
}

export function readSetGridPrefs() {
  return readPrefs(STORAGE_KEY);
}

export function writeSetGridPrefs(prefs) {
  writePrefs(STORAGE_KEY, prefs);
}

/** The same four choices, remembered separately for a chase binder. */
export function readBinderGridPrefs() {
  return readPrefs(BINDER_STORAGE_KEY);
}

export function writeBinderGridPrefs(prefs) {
  writePrefs(BINDER_STORAGE_KEY, prefs);
}

